import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GeoJSON, useMap, useMapEvents } from 'react-leaflet';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { GeoJSON as LeafletGeoJSON, Layer, Path } from 'leaflet';
import { api } from '@/lib/api-client';
import { outwardBbox } from '@/lib/mapBbox';
import { useMapStyle } from '@/components/map/mapColors';
import { useSubParcelsLayerEnabled } from './useSubParcelsLayer';
import './SubParcels.css';

/**
 * Orange overlay of subpolygons (receptions included), shared by every map
 * through `<MapLayerControl />`. A subpolygon sits on a green parcel, so it
 * needs a hue far from green to read as a separate thing at a glance, on
 * the street map and on satellite. Unattached ones are dashed.
 */
export const SUBPARCEL_STROKE = '#c2410c';
export const SUBPARCEL_FILL = '#fb923c';

/**
 * Above parcels (`overlayPane`, 400) so subpolygons paint over their parent
 * and win the click, below markers (600).
 */
const SUBPARCELS_PANE = 'subParcelsPane';
const SUBPARCELS_PANE_Z = 420;

/** Labels only from this zoom: below it a label is wider than its shape. */
const LABEL_MIN_ZOOM = 15;

const EMPTY: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

type SubParcelFeatureProps = {
  id: string;
  parcelId: string | null;
  ucf: string;
  number: string;
  source: string;
  parentUcf: string | null;
  raion: string | null;
  primarie: string | null;
  anul: number | null;
  sezon: 'PRIMAVARA' | 'TOAMNA' | null;
  parcele: string | null;
  suprafata: number;
  suprafataCalculata: number | null;
};

type SubParcelCollection = {
  type: 'FeatureCollection';
  features: Array<GeoJSON.Feature<GeoJSON.Geometry, SubParcelFeatureProps>>;
  truncated: boolean;
};


function escapeHtml(v: string): string {
  return v.replace(
    /[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] ?? c,
  );
}

export function SubParcelsMapLayer() {
  const { t } = useTranslation(['database']);
  const map = useMap();
  const { enabled } = useSubParcelsLayerEnabled();
  const [bbox, setBbox] = useState<string | null>(null);
  const layerRef = useRef<LeafletGeoJSON | null>(null);
  const subColor = useMapStyle().colors.subparcel;

  if (!map.getPane(SUBPARCELS_PANE)) {
    map.createPane(SUBPARCELS_PANE).style.zIndex = String(SUBPARCELS_PANE_Z);
  }

  const syncLabels = useCallback(() => {
    const show = map.getZoom() >= LABEL_MIN_ZOOM;
    layerRef.current?.eachLayer((l) => {
      if (show) l.openTooltip();
      else l.closeTooltip();
    });
  }, [map]);

  const syncBounds = useCallback(() => {
    const b = map.getBounds().pad(0.2);
    setBbox(outwardBbox([b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]));
  }, [map]);

  useMapEvents({
    moveend: syncBounds,
    zoomend: () => {
      syncBounds();
      syncLabels();
    },
  });
  useEffect(() => {
    syncBounds();
  }, [syncBounds]);

  // Viewport-bounded, never a paginated list (CLAUDE.md, "Map data caps").
  const query = useQuery<SubParcelCollection>({
    queryKey: ['subparcels', 'geojson', bbox],
    queryFn: async () =>
      api
        .get('subparcels/geojson', { searchParams: bbox ? { bbox } : undefined })
        .json<SubParcelCollection>(),
    staleTime: 60_000,
    // Keep drawing the last window while the next one loads, instead of
    // blanking the layer on every zoom.
    placeholderData: keepPreviousData,
    enabled: enabled && bbox !== null,
  });

  const bindFeature = useCallback(
    (feature: GeoJSON.Feature, layer: Layer) => {
      const p = feature.properties as SubParcelFeatureProps | null;
      if (!p) return;
      const rows: Array<[string, string]> = [
        ['UCF', p.ucf],
        [
          t('subparcels.parent', { defaultValue: 'Poligon' }),
          p.parentUcf ?? t('subparcels.unattached', { defaultValue: 'fără poligon' }),
        ],
        [t('subparcels.area', { defaultValue: 'Suprafață (ha)' }), p.suprafata.toFixed(2)],
      ];
      if (p.suprafataCalculata !== null && Math.abs(p.suprafataCalculata - p.suprafata) >= 0.005) {
        rows.push([
          t('subparcels.areaComputed', { defaultValue: 'Suprafață calculată (ha)' }),
          p.suprafataCalculata.toFixed(2),
        ]);
      }
      if (p.primarie) rows.push([t('subparcels.primarie', { defaultValue: 'Primăria' }), p.primarie]);
      if (p.raion) rows.push([t('subparcels.raion', { defaultValue: 'Raion' }), p.raion]);
      if (p.sezon && p.anul) {
        rows.push([
          t('subparcels.campaign', { defaultValue: 'Recepție' }),
          `${t(`subparcels.sezon.${p.sezon}`, { defaultValue: p.sezon === 'PRIMAVARA' ? 'Primăvara' : 'Toamna' })} ${p.anul}`,
        ]);
      } else if (p.anul) {
        rows.push([t('subparcels.year', { defaultValue: 'Anul' }), String(p.anul)]);
      }
      if (p.parcele) rows.push([t('subparcels.parcele', { defaultValue: 'Parcele' }), p.parcele]);
      layer.bindPopup(
        `<div style="font-size:12px"><div style="font-weight:600;margin-bottom:4px">${escapeHtml(
          t('subparcels.layerName', { defaultValue: 'Subpoligoane' }),
        )}</div>${rows
          .map(([k, v]) => `<div><b>${escapeHtml(k)}:</b> ${escapeHtml(v)}</div>`)
          .join('')}</div>`,
      );
      (layer as Path).bindTooltip(escapeHtml(p.ucf), {
        permanent: true,
        direction: 'center',
        className: 'subparcel-label',
        pane: 'tooltipPane',
      });
    },
    [t],
  );

  // Swap the data in place rather than remounting on a `key`: the
  // LayersControl registers the overlay's checkbox when the child layer
  // mounts and drops it when the child unmounts, so a remount per pan
  // would make the entry flicker out of the widget.
  useEffect(() => {
    const layer = layerRef.current;
    if (!layer || !query.data) return;
    layer.clearLayers();
    if (query.data?.features.length) {
      layer.addData({
        type: 'FeatureCollection',
        features: query.data.features,
      } as GeoJSON.FeatureCollection);
    }
    syncLabels();
  }, [query.data, syncLabels]);

  return (
    <GeoJSON
      ref={layerRef}
      data={EMPTY}
      pane={SUBPARCELS_PANE}
      style={(f) => ({
        color: subColor,
        weight: 2,
        fillColor: subColor,
        fillOpacity: 0.2,
        // Unattached (in no polygon, awaiting review): dashed.
        dashArray: (f?.properties as SubParcelFeatureProps | undefined)?.parcelId ? undefined : '6 4',
      })}
      onEachFeature={bindFeature}
    />
  );
}
